import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Mark } from '@/components/brand/Brand'
import { markBars } from '@/components/brand/brandPaths'
import { orbitDemo } from '@/components/motion/demos/orbitDemo'
import { radarDemo } from '@/components/motion/demos/radarDemo'
import { useIsClient, useMotion } from '@/components/motion/motion'
import { useDemo } from '@/components/motion/useDemo'
import { cx } from '@/lib/cx'

/* What changes: three chapters beside a sticky shield. The chapter nearest the middle of the screen
   builds its bar, and its illustration plays. */

const shieldLabels = [
  { n: 3, y: 13.5 },
  { n: 2, y: 31 },
  { n: 1, y: 49 },
]

const splitStep = (index: number) => ({ '--i': index }) as CSSProperties

export const ChangesSection = () => {
  const { paused, reduceMotion } = useMotion()
  const isClient = useIsClient()
  const listRef = useRef<HTMLDivElement>(null)
  const radarRef = useRef<HTMLDivElement>(null)
  const orbitRef = useRef<HTMLDivElement>(null)
  const radar = useDemo(radarRef, radarDemo)
  const orbit = useDemo(orbitRef, orbitDemo)
  const [active, setActive] = useState<number | null>(null)
  // The shield keeps the last chapter read; it starts complete.
  const [built, setBuilt] = useState(3)
  const dispatched = useRef<number | null | undefined>(undefined)
  const animating = isClient && !reduceMotion && !paused

  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const chapters = Array.from(list.querySelectorAll<HTMLElement>('[data-chapter]'))

    let frame = 0
    const pick = () => {
      frame = 0
      const viewportHeight = window.innerHeight || 800
      const focus = viewportHeight * 0.5
      let best: number | null = null
      let bestDistance = Infinity
      for (const chapter of chapters) {
        const first = chapter.firstElementChild
        const last = chapter.lastElementChild
        if (!first || !last) continue
        const top = first.getBoundingClientRect().top
        const bottom = last.getBoundingClientRect().bottom
        // Only chapters actually on screen.
        if (bottom < viewportHeight * 0.25 || top > viewportHeight * 0.75) continue
        const distance = Math.abs((top + bottom) / 2 - focus)
        if (distance < bestDistance) {
          bestDistance = distance
          best = Number(chapter.dataset.chapter)
        }
      }
      setActive(best)
      if (best !== null) setBuilt(best)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(pick)
    }

    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    schedule()
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [])

  // The active chapter's illustration plays. The others wait at their start while chapters animate,
  // so they play again when the reader arrives; otherwise they show their finished state.
  useEffect(() => {
    const demos = [
      { chapter: 2, controls: radar.current },
      { chapter: 3, controls: orbit.current },
    ]
    if (dispatched.current === undefined) {
      demos.forEach(({ controls }) => controls?.fin())
    } else if (dispatched.current !== active) {
      for (const { chapter, controls } of demos) {
        if (chapter === active) controls?.run()
        else if (animating) controls?.pre()
        else controls?.fin()
      }
    }
    dispatched.current = active
  }, [active, animating, radar, orbit])

  useEffect(() => {
    if (!paused) return
    radar.current?.fin()
    orbit.current?.fin()
  }, [paused, radar, orbit])

  // Layout depends on the stage size: settle to the finished state after a resize.
  useEffect(() => {
    let timer = 0
    const onResize = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        radar.current?.fin()
        orbit.current?.fin()
      }, 200)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('resize', onResize)
    }
  }, [radar, orbit])

  return (
    <section id="how" className="x-changes">
      <div className="x-wrap">
        <div className={cx('x-chapters', animating && 'x-anim')} data-active={built}>
          <div className="x-shield-col" aria-hidden="true">
            <div className="x-shield-sticky">
              <svg className="x-shield" viewBox="0 -2 66 64.72">
                {markBars.map((bar, index) => (
                  <path key={bar.variable} className={`sb sb${3 - index}`} d={bar.d} />
                ))}
                {shieldLabels.map((label) => (
                  <text key={label.n} className={`sn sn${label.n}`} x="56" y={label.y}>
                    {`0${label.n}`}
                  </text>
                ))}
              </svg>
            </div>
          </div>

          <div ref={listRef} className="x-chapter-list">
            <article className={cx('x-chapter', active === 1 && 'is-active')} data-chapter="1">
              <p className="x-ch-n">
                <span>01</span>Start
              </p>
              <h3>Higher margins, by moving production to Justine</h3>
              <p className="x-ch-body">
                Justine (formerly UnitedOS) brings practice management and accounting production into one product. It’s built so
                software prepares the routine work and your accountants sign off.
              </p>
              <div className="x-demo x-split" aria-hidden="true">
                <div className="x-split-col x-split-os">
                  <p className="x-demo-label">
                    <Mark />
                    Justine prepares
                  </p>
                  <ul>
                    <li style={splitStep(0)}>Collect documents</li>
                    <li style={splitStep(1)}>Categorize</li>
                    <li style={splitStep(2)}>Reconcile</li>
                    <li style={splitStep(3)}>Draft the books</li>
                  </ul>
                </div>
                <div className="x-split-col x-split-you">
                  <p className="x-demo-label">Your accountant</p>
                  <ul>
                    <li style={splitStep(4)}>Review</li>
                    <li style={splitStep(5)}>Judgment calls</li>
                    <li className="x-sign" style={splitStep(6)}>
                      Signs off
                    </li>
                  </ul>
                </div>
              </div>
            </article>

            <article className={cx('x-chapter', active === 2 && 'is-active')} data-chapter="2">
              <p className="x-ch-n">
                <span>02</span>As capacity grows
              </p>
              <h3>Your firm grows through acquisitions we finance</h3>
              <p className="x-ch-body">
                Our deal team finds, finances and closes client books chosen with you, and they join your firm under your name.
              </p>
              <div className="x-demo x-stagebox" aria-hidden="true">
                <div ref={radarRef} className="m-stage m-radar" />
              </div>
            </article>

            <article className={cx('x-chapter', active === 3 && 'is-active')} data-chapter="3">
              <p className="x-ch-n">
                <span>03</span>Over time
              </p>
              <h3>More to offer your clients</h3>
              <p className="x-ch-body">
                Financial products and specialist expertise close to the books, which your team can offer without retraining or
                new hires.
              </p>
              <div className="x-demo x-stagebox" aria-hidden="true">
                <div ref={orbitRef} className="m-stage m-orbit" />
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  )
}
